import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { EVENT_TYPES, parseInsuranceFile } from '../../../services/investmentPlanningService';
import { getAchatDownPaymentSources } from '../../../services/investmentProjectionEngine';
import { computeCollateralCapacity, DEFAULT_COLLATERAL_LTV } from '../../../services/planningTax/insuranceCollateral';

const currentYear = new Date().getFullYear();

function newDpSourceId() {
  return `dps-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

const DP_SOURCE_TYPE_OPTIONS = [
  { value: 'liquidites', label: 'Liquidités / épargne' },
  { value: 'refinancement', label: 'Refinancement (hypothèque)' },
  { value: 'collateral_bancaire', label: 'Prêt collatéral (assurance participative)' },
];

const EMPTY_EVENT = {
  name: '', type: 'refinancement', year: String(currentYear + 1), notes: '',
  details: {},
};

const PlanningEvents = ({ events = [], entities = [], assets = [], debts = [], properties = [], projectionData = [], planningHorizonYears = 25, onAdd, onUpdate, onDelete, formatCurrency }) => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);

  // Insurance import state
  const [parsedInsurance, setParsedInsurance] = useState(null);
  const [insuranceFileName, setInsuranceFileName] = useState('');
  const [insuranceError, setInsuranceError] = useState('');
  const [currentPolicyYear, setCurrentPolicyYear] = useState(1);
  const [draggedEventId, setDraggedEventId] = useState(null);
  const insuranceFileRef = useRef(null);

  const handleInsuranceFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setInsuranceError('');
    setInsuranceFileName(file.name);
    setForm(prev => ({
      ...prev,
      name: prev.name || file.name.replace(/\.(xlsx|xls|csv)$/i, ''),
    }));

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const parsed = parseInsuranceFile(workbook);
        if (parsed.length === 0) {
          setInsuranceError("Aucune donnée trouvée. Vérifiez que le fichier contient les colonnes 'Année', 'Valeur de rachat totale'.");
          return;
        }
        setParsedInsurance(parsed);
        setCurrentPolicyYear(parsed[0]?.year || 1);
      } catch (err) {
        setInsuranceError(`Erreur de lecture: ${err.message}`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const resetInsuranceState = () => {
    setParsedInsurance(null);
    setInsuranceFileName('');
    setInsuranceError('');
    setCurrentPolicyYear(1);
    if (insuranceFileRef.current) insuranceFileRef.current.value = '';
  };

  const entityOptions = entities.map(e => ({ value: e.id, label: e.name }));

  // Immobilier assets for refinancing / sale (existing + planned from achat events)
  const immobilierAssets = assets.filter(a => a.type === 'immobilier');

  // Build list of future planned assets from 'achat' events (enabled only)
  const futureAchatAssets = events
    .filter(e => e.type === 'achat' && !e.details?.disabled && e.details?.propertyName)
    .map(e => ({
      id: `event_${e.id}`,
      name: e.details.propertyName || e.name,
      currentBalance: parseFloat(e.details.purchasePrice) || 0,
      type: 'immobilier',
      _sourceEventId: e.id,
      _sourceYear: parseInt(e.year),
    }));

  // Combined list: existing + future planned
  const allImmobilierAssets = [...immobilierAssets, ...futureAchatAssets];

  const immobilierOptions = allImmobilierAssets.map(a => ({
    value: a.id,
    label: `${a.name} (${formatCurrency(a.currentBalance)})${a._sourceYear ? ` — An ${a._sourceYear}` : ''}`,
  }));

  /** Nom affichable pour une police (bilan ou id projeté insurance-{eventId}). */
  const resolveCollateralInsuranceName = (collateralAssetId) => {
    const fromBilan = assets.find(a => a.id === collateralAssetId);
    if (fromBilan?.name) return fromBilan.name;
    const m = String(collateralAssetId || '').match(/^insurance-(.+)$/);
    if (!m) return 'assurance';
    const ev = events.find(e => String(e.id) === m[1] && e.type === 'assurance_participative');
    return ev?.details?.policyName || ev?.name || 'assurance';
  };

  // Destination options for fund routing (vente / refinancement cash-out)
  const nonImmobilierAssets = assets.filter(a => a.type !== 'immobilier' && a.type !== 'assurance_participative');

  const liquidAccountsForPremium = assets.filter(a => a.type !== 'immobilier' && a.type !== 'assurance_participative');
  const premiumDebitSelectOptions = liquidAccountsForPremium.map(a => ({
    value: a.id,
    label: `${a.name} (${entities.find(e => e.id === a.entityId)?.name || '—'})`,
  }));
  const fundDestinationOptions = [
    { value: '__new__', label: '+ Créer un nouveau compte' },
    ...nonImmobilierAssets.map(a => ({
      value: a.id,
      label: `${a.name} (${formatCurrency(a.currentBalance)})`,
    })),
  ];

  const FUND_ACCOUNT_TYPES = [
    { value: 'non_enregistre', label: 'Compte bancaire / Non-enregistré' },
    { value: 'corporatif', label: 'Compte corporatif' },
    { value: 'celi', label: 'CELI' },
    { value: 'reer', label: 'REER' },
    { value: 'autre', label: 'Autre' },
  ];

  // Hypothèque debts linked to immobilier for refinancing
  const hypothequeDebts = debts.filter(d => d.type === 'hypotheque');

  /**
   * Les immeubles issus d'un événement « achat » ont l'id `event_<id>` ; l'hypothèque est créée
   * dans le moteur (id `mortgage-<id>`) mais n'est pas une ligne du bilan — on la synthétise pour l'UI.
   */
  const syntheticMortgageFromAchatEvent = (assetId) => {
    if (!assetId || !String(assetId).startsWith('event_')) return null;
    const rawId = String(assetId).replace(/^event_/, '');
    const ev = events.find(e => e.type === 'achat' && String(e.id) === rawId && !e.details?.disabled);
    if (!ev) return null;
    const d = ev.details || {};
    const price = parseFloat(d.purchasePrice) || 0;
    const downPct = (parseFloat(d.downPaymentPercent) || 20) / 100;
    const mortgageAmt = price * (1 - downPct);
    if (mortgageAmt <= 0) return null;
    return {
      id: `mortgage-${ev.id}`,
      name: `Hypothèque - ${d.propertyName || ev.name}`,
      type: 'hypotheque',
      currentBalance: mortgageAmt,
      linkedAssetId: assetId,
      interestRate: parseFloat(d.interestRate) || 5,
      amortizationRemaining: parseFloat(d.amortization) || 25,
      monthlyPayment: 0,
      entityId: d.entityId,
    };
  };

  const getDebtsForAsset = (assetId) => {
    if (!assetId) return hypothequeDebts;
    const linked = hypothequeDebts.filter(db => db.linkedAssetId === assetId);
    if (linked.length > 0) return linked;
    const synthetic = syntheticMortgageFromAchatEvent(assetId);
    return synthetic ? [synthetic] : [];
  };

  const yearOptionsCount = Math.min(61, Math.max(26, (planningHorizonYears || 25) + 1));
  const yearOptions = Array.from({ length: yearOptionsCount }, (_, i) => ({
    value: String(currentYear + i),
    label: String(currentYear + i),
  }));

  const setDetail = (key, value) => {
    setForm(prev => ({ ...prev, details: { ...prev.details, [key]: value } }));
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    const assuranceHasSchedule = (parsedInsurance?.length > 0) || (form.details?.insuranceSchedule?.length > 0);
    if (form.type === 'assurance_participative' && assuranceHasSchedule && form.details?.includePremiumsInCashflow !== false) {
      if (!form.details?.premiumDebitAssetId) return;
    }
    const data = { ...form, year: parseInt(form.year) };

    // If assurance_participative with imported data, add schedule to details
    if (form.type === 'assurance_participative' && parsedInsurance) {
      const startIdx = parsedInsurance.findIndex(d => d.year === currentPolicyYear);
      const idx = startIdx >= 0 ? startIdx : 0;
      const currentRow = parsedInsurance[idx] || parsedInsurance[0] || {};
      data.details = {
        ...data.details,
        insuranceSchedule: parsedInsurance,
        insuranceCurrentYearIdx: idx,
        initialSurrenderValue: currentRow.surrenderValue || 0,
        totalInsurance: currentRow.totalInsurance || 0,
        annualPremium: currentRow.premiumPaid || 0,
        policyName: form.name,
        includePremiumsInCashflow: form.details?.includePremiumsInCashflow !== false,
        premiumDebitAssetId: form.details?.includePremiumsInCashflow !== false ? (form.details?.premiumDebitAssetId || '') : '',
      };
    } else if (form.type === 'assurance_participative') {
      data.details = {
        ...data.details,
        includePremiumsInCashflow: form.details?.includePremiumsInCashflow !== false,
        premiumDebitAssetId: form.details?.includePremiumsInCashflow !== false ? (form.details?.premiumDebitAssetId || '') : '',
      };
    }

    if (form.type === 'changement_contributions') {
      const changes = (form.details?.changes || [])
        .filter(ch => ch?.assetId)
        .map(ch => ({
          assetId: ch.assetId,
          monthlyContribution: ch.monthlyContribution,
          avgAnnualReturn: ch.avgAnnualReturn,
        }));
      if (changes.length === 0) return;
      data.details = { ...form.details, changes };
    }

    if (editingId) { onUpdate(editingId, data); }
    else { onAdd(data); }
    setForm(EMPTY_EVENT);
    setShowForm(false);
    setEditingId(null);
    resetInsuranceState();
  };

  const handleEdit = (item) => {
    let details = item.details || {};
    if (item.type === 'achat') {
      const price = parseFloat(details.purchasePrice) || 0;
      const dpPct = parseFloat(details.downPaymentPercent) || 20;
      const dpAmt = price * (dpPct / 100);
      details = {
        ...details,
        downPaymentSources: getAchatDownPaymentSources(details, dpAmt),
      };
    }
    if (item.type === 'changement_contributions' && (!details.changes || details.changes.length === 0)) {
      details = { ...details, changes: [{ assetId: '', monthlyContribution: '', avgAnnualReturn: '' }] };
    }
    setForm({
      name: item.name || '', type: item.type || 'refinancement',
      year: String(item.year || currentYear + 1), notes: item.notes || '',
      details,
    });
    setEditingId(item.id);
    setShowForm(true);

    if (item.type === 'assurance_participative' && details.insuranceSchedule?.length) {
      setParsedInsurance(details.insuranceSchedule);
      const idx = Math.min(
        Math.max(0, parseInt(details.insuranceCurrentYearIdx, 10) || 0),
        details.insuranceSchedule.length - 1,
      );
      const row = details.insuranceSchedule[idx] || details.insuranceSchedule[0];
      setCurrentPolicyYear(row?.year ?? details.insuranceSchedule[0]?.year ?? 1);
    } else if (item.type === 'assurance_participative') {
      setParsedInsurance(null);
      setCurrentPolicyYear(1);
    }
  };

  const handleCancel = () => { setForm(EMPTY_EVENT); setShowForm(false); setEditingId(null); resetInsuranceState(); };
  const openForm = () => { setForm(EMPTY_EVENT); setEditingId(null); setShowForm(true); resetInsuranceState(); };

  const handleSelectAssetForRefi = (assetId) => {
    const asset = allImmobilierAssets.find(a => a.id === assetId);
    const linkedDebts = getDebtsForAsset(assetId);

    // Use projected value at event year
    const eventYear = parseInt(form.year) || 0;
    const projRow = projectionData.find(p => p.year === eventYear);
    const projectedValue = projRow?.assetProjected?.[assetId] || (asset ? parseFloat(asset.currentBalance) : 0);

    setDetail('assetId', assetId);
    if (asset) {
      setForm(prev => ({
        ...prev,
        name: prev.name || `Refinancement - ${asset.name}`,
        details: {
          ...prev.details,
          assetId,
          propertyValue: String(Math.round(projectedValue) || ''),
          debtId: linkedDebts.length === 1 ? linkedDebts[0].id : prev.details?.debtId || '',
        },
      }));
    }
  };

  const handleSelectAssetForSale = (assetId) => {
    const asset = allImmobilierAssets.find(a => a.id === assetId);
    const linkedDebts = getDebtsForAsset(assetId);

    // Use projected value at event year
    const eventYear = parseInt(form.year) || 0;
    const projRow = projectionData.find(p => p.year === eventYear);
    const projectedValue = projRow?.assetProjected?.[assetId] || (asset ? parseFloat(asset.currentBalance) : 0);

    setForm(prev => ({
      ...prev,
      name: prev.name || `Vente - ${asset?.name || ''}`,
      details: {
        ...prev.details,
        assetId,
        debtId: linkedDebts.length === 1 ? linkedDebts[0].id : '',
        salePrice: String(Math.round(projectedValue) || ''),
      },
    }));
  };

  const getTypeLabel = (type) => EVENT_TYPES.find(t => t.value === type)?.label || type;
  const getTypeIcon = (type) => ({ refinancement: 'RefreshCw', achat: 'Home', vente: 'DollarSign', renovation: 'Hammer', produit_financier: 'Briefcase', assurance_participative: 'Shield', changement_contributions: 'PiggyBank', cashflow_direction: 'Activity', autre: 'MoreHorizontal' }[type] || 'Circle');
  const getTypeColor = (type) => ({ refinancement: '#3b82f6', achat: '#22c55e', vente: '#f97316', renovation: '#d946ef', produit_financier: '#8b5cf6', assurance_participative: '#0ea5e9', changement_contributions: '#6366f1', cashflow_direction: '#10b981', autre: '#64748b' }[type] || '#64748b');

  const handleToggleEvent = (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    const isCurrentlyDisabled = event.details?.disabled === true;
    const newDisabled = !isCurrentlyDisabled;

    // Update this event
    onUpdate(eventId, { ...event, details: { ...event.details, disabled: newDisabled } });

    // If disabling an 'achat' event, cascade disable all events that reference its asset
    if (event.type === 'achat' && newDisabled) {
      const futureAssetId = `event_${eventId}`;
      events.forEach(e => {
        if (e.id !== eventId && e.details?.assetId === futureAssetId && !e.details?.disabled) {
          onUpdate(e.id, { ...e, details: { ...e.details, disabled: true } });
        }
      });
    }
    // If re-enabling an 'achat' event, re-enable its linked events
    if (event.type === 'achat' && !newDisabled) {
      const futureAssetId = `event_${eventId}`;
      events.forEach(e => {
        if (e.id !== eventId && e.details?.assetId === futureAssetId && e.details?.disabled) {
          onUpdate(e.id, { ...e, details: { ...e.details, disabled: false } });
        }
      });
    }
  };

  const sortedEvents = [...events].sort((a, b) => (parseInt(a.year) || 0) - (parseInt(b.year) || 0));
  const timelineYears = Array.from(
    new Set(
      sortedEvents
        .map(e => parseInt(e.year, 10))
        .filter(y => Number.isFinite(y)),
    ),
  ).sort((a, b) => a - b);

  const moveEventToYear = (eventId, year) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    onUpdate(eventId, { ...event, year: parseInt(year, 10) });
  };

  // Calculate CAGR between event year and final projection year
  // If funded by refinancement: return is relative to original down payment of refinanced property
  // If funded by cash: return is relative to the net worth growth from that year
  const getEventCAGR = (event) => {
    if (!projectionData || projectionData.length < 2) return null;
    const evtYearInt = parseInt(event.year);
    const evtRow = projectionData.find(p => p.year === evtYearInt);
    const finalRow = projectionData[projectionData.length - 1];
    if (!evtRow || !finalRow || evtRow.year === finalRow.year) return null;
    const n = finalRow.year - evtRow.year;
    if (n <= 0) return null;

    const d = event.details || {};

    const achatDp = (parseFloat(d.purchasePrice) || 0) * ((parseFloat(d.downPaymentPercent) || 20) / 100);
    const achatSources = event.type === 'achat' ? getAchatDownPaymentSources(d, achatDp) : [];
    const firstRefiSource = achatSources.find(s => s.type === 'refinancement');
    const refiAssetIdForCagr = firstRefiSource?.refiAssetId || d.refiAssetId;

    // For achat events funded by refinancement
    if (event.type === 'achat' && (firstRefiSource || d.downPaymentSource === 'refinancement' || d.downPaymentSource === 'mixte')) {
      // The "investment base" is the original down payment of the property being refinanced
      const refiAsset = [...assets, ...futureAchatAssets].find(a => a.id === refiAssetIdForCagr);
      // Find the original achat event for the refinanced property
      let originalDownPayment = 0;
      if (refiAsset) {
        // Check if it's a future asset from an achat event
        const sourceEvent = events.find(e => `event_${e.id}` === refiAssetIdForCagr && e.type === 'achat');
        if (sourceEvent) {
          const sp = sourceEvent.details || {};
          const origPrice = parseFloat(sp.purchasePrice) || 0;
          const origPct = (parseFloat(sp.downPaymentPercent) || 20) / 100;
          originalDownPayment = origPrice * origPct;
        } else {
          // Existing asset — estimate from current value * typical down payment
          const origAsset = assets.find(a => a.id === refiAssetIdForCagr);
          if (origAsset) {
            // Try to find linked debt to estimate original down payment
            const linkedDebt = debts.find(dd => dd.linkedAssetId === origAsset.id);
            const origValue = parseFloat(origAsset.currentBalance) || 0;
            const origDebt = linkedDebt ? parseFloat(linkedDebt.currentBalance) || 0 : 0;
            originalDownPayment = origValue - origDebt; // equity = value - debt ≈ original down payment after growth
          }
        }
      }

      if (originalDownPayment > 0) {
        // Calculate the additional net worth generated from the event year to final year
        const additionalNetWorth = finalRow.netWorth - evtRow.netWorth;
        if (additionalNetWorth <= 0) return null;
        // CAGR of the additional return relative to the original down payment
        const totalReturnRatio = additionalNetWorth / originalDownPayment;
        if ((1 + totalReturnRatio) <= 0) return null;
        return (Math.pow(1 + totalReturnRatio, 1 / n) - 1) * 100;
      }
    }

    // Default: CAGR of net worth growth from event year
    const nwStart = evtRow.netWorth;
    const nwEnd = finalRow.netWorth;
    if (nwStart <= 0 || nwEnd <= 0) return null;
    return (Math.pow(nwEnd / nwStart, 1 / n) - 1) * 100;
  };

  const getCashflowDirectionSummary = (details) => {
    const routingBySource = details?.routingBySource || {};
    const accountIds = new Set();
    let hasSansImpots = false;

    for (const r of Object.values(routingBySource)) {
      if (r?.dividendTaxMode === 'personnel') hasSansImpots = true;
      const destinations = r?.destinations || [];
      for (const d of destinations) {
        if (d?.assetId) accountIds.add(d.assetId);
      }
    }

    const names = Array.from(accountIds)
      .map(id => assets.find(a => a.id === id)?.name || null)
      .filter(Boolean);
    const parts = names.slice(0, 2);
    const more = names.length > 2 ? `+${names.length - 2}` : '';

    const modeLabel = hasSansImpots ? 'Sans impôt' : 'Dividendes';
    return `${modeLabel}${parts.length ? ` • ${parts.join(', ')}${more ? ` ${more}` : ''}` : ''}`;
  };

  const renderDetailFields = () => {
    switch (form.type) {
      case 'refinancement': {
        const selectedAssetDebts = form.details?.assetId
          ? getDebtsForAsset(form.details.assetId)
          : hypothequeDebts;

        // Use projected debt balances at event year
        const refiEventYear = parseInt(form.year) || 0;
        const refiProjRow = projectionData.find(p => p.year === refiEventYear);

        const debtOptions = selectedAssetDebts.map(d => {
          const projBal = refiProjRow?.debtProjected?.[d.id] || parseFloat(d.currentBalance) || 0;
          return {
            value: d.id,
            label: `${d.name} (${formatCurrency(projBal)}${refiEventYear > 0 ? ` à l'an ${refiEventYear}` : ''})`,
          };
        });

        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select label="Propriété à refinancer" value={form.details.assetId || ''} onChange={handleSelectAssetForRefi}
                options={immobilierOptions} placeholder="Sélectionner la propriété..." required data-testid="event-refi-asset" />
              <Select label="Hypothèque associée" value={form.details.debtId || ''} onChange={v => setDetail('debtId', v)}
                options={debtOptions} placeholder={debtOptions.length === 0 ? 'Aucune hypothèque liée' : 'Sélectionner...'} data-testid="event-refi-debt" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Input label="Valeur propriété ($)" type="number" value={form.details.propertyValue || ''} onChange={e => setDetail('propertyValue', e.target.value)} placeholder="1 200 000" />
              <Input label="Nouveau ratio prêt-valeur (%)" type="number" value={form.details.newLTV || ''} onChange={e => setDetail('newLTV', e.target.value)} placeholder="80" />
              <Input label="Nouveau taux (%)" type="number" value={form.details.newRate || ''} onChange={e => setDetail('newRate', e.target.value)} placeholder="4.5" />
              <Input label="Nouvel amortissement (ans)" type="number" value={form.details.newAmortization || ''} onChange={e => setDetail('newAmortization', e.target.value)} placeholder="25" />
            </div>
            <Select label="Détenteur" value={form.details.entityId || ''} onChange={v => setDetail('entityId', v)} options={entityOptions} placeholder="Sélectionner..." />

            {/* Fund destination for refinancing cash-out */}
            <div className="border border-border rounded-lg p-4 space-y-3" data-testid="refi-fund-destination">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="ArrowRightLeft" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Destination des fonds (cash-out)</span>
              </div>
              <Select
                label="Diriger les fonds vers"
                value={form.details.fundDestinationId || '__new__'}
                onChange={v => setDetail('fundDestinationId', v)}
                options={fundDestinationOptions}
                data-testid="refi-fund-destination-select"
              />
              {(form.details.fundDestinationId === '__new__' || !form.details.fundDestinationId) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    label="Nom du nouveau compte"
                    value={form.details.newAccountName || ''}
                    onChange={e => setDetail('newAccountName', e.target.value)}
                    placeholder="Ex: Compte placement refi"
                    data-testid="refi-new-account-name"
                  />
                  <Select
                    label="Type de compte"
                    value={form.details.newAccountType || 'non_enregistre'}
                    onChange={v => setDetail('newAccountType', v)}
                    options={FUND_ACCOUNT_TYPES}
                    data-testid="refi-new-account-type"
                  />
                </div>
              )}
            </div>
          </>
        );
      }
      case 'achat': {
        const eventYear = parseInt(form.year) || 0;
        const projRow = projectionData.find(p => p.year === eventYear);

        const liquidAssets = assets.filter(a => a.type !== 'immobilier');
        const getProjectedAssetValue = (assetId, fallback) => {
          return projRow?.assetProjected?.[assetId] ?? (parseFloat(fallback) || 0);
        };
        const liquidOptions = liquidAssets
          .map(a => ({
            value: a.id,
            label: `${a.name} (${formatCurrency(getProjectedAssetValue(a.id, a.currentBalance))})`,
            projected: getProjectedAssetValue(a.id, a.currentBalance),
          }))
          .filter(o => (parseFloat(o.projected) || 0) > 0);
        // Bilan + polices créées uniquement par événement (id projeté: insurance-{id événement})
        const insuranceFromBilan = assets.filter(a => a.type === 'assurance_participative');
        const insuranceFromEvents = events
          .filter(e =>
            e.type === 'assurance_participative'
            && !e.details?.disabled
            && e.details?.insuranceSchedule?.length
            && (parseInt(e.year, 10) || 0) <= eventYear
          )
          .map(e => {
            const idx = e.details.insuranceCurrentYearIdx || 0;
            const row = e.details.insuranceSchedule[idx] || e.details.insuranceSchedule[0] || {};
            return {
              id: `insurance-${e.id}`,
              name: e.details.policyName || e.name,
              type: 'assurance_participative',
              currentBalance: parseFloat(row.surrenderValue) || 0,
            };
          });
        const insuranceIds = new Set();
        const insuranceAssets = [...insuranceFromBilan, ...insuranceFromEvents].filter((a) => {
          if (insuranceIds.has(a.id)) return false;
          insuranceIds.add(a.id);
          return true;
        });
        const insuranceOptions = insuranceAssets
          .map(a => ({
            value: a.id,
            label: `${a.name} (${formatCurrency(getProjectedAssetValue(a.id, a.currentBalance))})`,
            projected: getProjectedAssetValue(a.id, a.currentBalance),
          }))
          .filter(o => (parseFloat(o.projected) || 0) > 0);

        const existingPropertyOptions = [
          { value: '', label: 'Saisie manuelle' },
          ...properties.map(p => ({
            value: p.id,
            label: `${p.address || 'Sans adresse'} — ${p.property_type || ''} (${p.units || 1} logements)`,
          })),
          { value: '__wizard__', label: '+ Nouvelle propriété (assistant d\'analyse)' },
        ];

        const purchasePrice = parseFloat(form.details.purchasePrice) || 0;
        const downPct = parseFloat(form.details.downPaymentPercent) || 0;
        const downPaymentAmount = purchasePrice * (downPct / 100);

        const downPaymentSources = (Array.isArray(form.details.downPaymentSources) && form.details.downPaymentSources.length > 0
          ? form.details.downPaymentSources
          : getAchatDownPaymentSources(form.details, downPaymentAmount));

        const patchDownPaymentSource = (idx, patch) => {
          setForm(prev => {
            const list = [...(prev.details.downPaymentSources || downPaymentSources)];
            list[idx] = { ...list[idx], ...patch };
            return { ...prev, details: { ...prev.details, downPaymentSources: list } };
          });
        };

        const addDownPaymentSource = () => {
          setForm(prev => ({
            ...prev,
            details: {
              ...prev.details,
              downPaymentSources: [...(prev.details.downPaymentSources || downPaymentSources), {
                id: newDpSourceId(),
                type: 'liquidites',
                amount: '',
                liquidSourceId: '',
              }],
            },
          }));
        };

        const removeDownPaymentSource = (idx) => {
          setForm(prev => {
            const list = [...(prev.details.downPaymentSources || [])];
            list.splice(idx, 1);
            const next = list.length ? list : [{ id: newDpSourceId(), type: 'liquidites', amount: '', liquidSourceId: '' }];
            return { ...prev, details: { ...prev.details, downPaymentSources: next } };
          });
        };

        /** Prior encumbrance on the policy (excluding this achat's synthetic collateral loan in the projection snapshot). */
        const getCollateralPreviewForInsuranceAsset = (assetId) => {
          if (!assetId) return { vr: 0, existingDebt: 0, maxLoan: 0 };
          const ins = insuranceAssets.find(a => a.id === assetId);
          const vr = projRow?.assetProjected?.[assetId] || parseFloat(ins?.currentBalance) || 0;
          const assetKey = `asset:${assetId}`;
          const projectedLinkedTotal = projRow?.debtProjected?.[assetKey];
          const rawLinked = projectedLinkedTotal !== undefined && projectedLinkedTotal !== null
            ? projectedLinkedTotal
            : debts
              .filter(dd => dd.linkedAssetId === assetId)
              .reduce((s, dd) => s + (parseFloat(dd.currentBalance) || 0), 0);
          const thisEventKey = editingId ? `collateral-loan-${editingId}` : null;
          const thisEventBal = thisEventKey && projRow?.debtProjected
            ? (projRow.debtProjected[thisEventKey] || 0)
            : 0;
          const existingDebt = Math.max(0, rawLinked - thisEventBal);
          const policyForCap = ins ? { ...ins, id: assetId, currentBalance: vr } : { id: assetId, currentBalance: vr, type: 'assurance_participative' };
          const cap = computeCollateralCapacity(policyForCap, debts.filter((dd) => {
            if (dd.linkedAssetId !== assetId) return false;
            if (thisEventKey && dd.id === thisEventKey) return false;
            return true;
          }));
          return { vr, existingDebt: cap.linkedDebt, maxLoan: cap.availableCapacity, ltvPercent: cap.ltvPercent };
        };

        const sumAllocated = downPaymentSources.reduce((acc, s) => acc + (parseFloat(s.amount) || 0), 0);
        const allocationMismatch = downPaymentSources.length > 1 && downPaymentAmount > 0
          && Math.abs(sumAllocated - downPaymentAmount) > 1;

        const handleSelectExistingProperty = (propId) => {
          if (propId === '__wizard__') {
            navigate('/property-analysis-wizard?stage=acquired&from=planning');
            return;
          }
          if (!propId) {
            // Manual — clear prefilled fields
            setForm(prev => ({
              ...prev,
              details: { ...prev.details, linkedPropertyId: '', propertyName: '', purchasePrice: '', downPaymentPercent: '20', interestRate: '', amortization: '', monthlyRevenue: '', monthlyExpenses: '' },
            }));
            return;
          }
          const prop = properties.find(p => p.id === propId);
          if (!prop) return;
          setForm(prev => ({
            ...prev,
            name: prev.name || `Achat - ${prop.address || 'Propriété'}`,
            details: {
              ...prev.details,
              linkedPropertyId: propId,
              propertyName: prop.address || '',
              purchasePrice: String(parseFloat(prop.purchase_price) || ''),
              downPaymentPercent: String(parseFloat(prop.down_payment_percent) || 20),
              interestRate: String(parseFloat(prop.interest_rate) || ''),
              amortization: String(parseInt(prop.amortization_period) || 25),
              monthlyRevenue: String(parseFloat(prop.total_monthly_revenue) || ''),
              monthlyExpenses: String(parseFloat(prop.total_monthly_expenses) || ''),
            },
          }));
        };

        return (
          <>
            {/* Property source: existing analysis or manual */}
            <div className="border border-border rounded-lg p-4 space-y-3" data-testid="achat-property-source">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Home" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Propriété</span>
              </div>
              <Select
                label="Source"
                value={form.details.linkedPropertyId || ''}
                onChange={handleSelectExistingProperty}
                options={existingPropertyOptions}
                data-testid="achat-property-select"
              />
              {!form.details.linkedPropertyId && (
                <div className="flex items-center gap-3 p-2.5 rounded-lg border border-success/20 bg-success/5">
                  <Icon name="Sparkles" size={16} className="text-success shrink-0" />
                  <p className="text-xs text-muted-foreground flex-1">Vous pouvez aussi utiliser l'assistant d'analyse pour créer un immeuble complet.</p>
                  <Button size="sm" variant="outline" iconName="ExternalLink" iconPosition="right"
                    onClick={() => navigate('/property-analysis-wizard?stage=acquired&from=planning')}
                    data-testid="event-open-wizard-btn">
                    Assistant
                  </Button>
                </div>
              )}
            </div>

            {/* Property details */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Input label="Nom de la propriété" value={form.details.propertyName || ''} onChange={e => setDetail('propertyName', e.target.value)} placeholder="Triplex Sherbrooke" />
              <Input label="Prix d'achat ($)" type="number" value={form.details.purchasePrice || ''} onChange={e => setDetail('purchasePrice', e.target.value)} placeholder="800 000" />
              <Input label="Mise de fonds (%)" type="number" value={form.details.downPaymentPercent || ''} onChange={e => setDetail('downPaymentPercent', e.target.value)} placeholder="20" />
              <Input label="Taux d'intérêt (%)" type="number" value={form.details.interestRate || ''} onChange={e => setDetail('interestRate', e.target.value)} placeholder="5" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="Amortissement (ans)" type="number" value={form.details.amortization || ''} onChange={e => setDetail('amortization', e.target.value)} placeholder="25" />
              <Input label="Revenu mensuel estimé ($)" type="number" value={form.details.monthlyRevenue || ''} onChange={e => setDetail('monthlyRevenue', e.target.value)} placeholder="4 000" />
              <Input label="Dépenses mensuelles ($)" type="number" value={form.details.monthlyExpenses || ''} onChange={e => setDetail('monthlyExpenses', e.target.value)} placeholder="1 200" />
            </div>
            <Select label="Détenteur" value={form.details.entityId || ''} onChange={v => setDetail('entityId', v)} options={entityOptions} placeholder="Sélectionner..." />

            {/* Down payment source — always visible */}
            <div className="border border-border rounded-lg p-4 space-y-3" data-testid="down-payment-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="Wallet" size={16} className="text-primary" />
                  <span className="text-sm font-semibold text-foreground">Source de la mise de fonds</span>
                </div>
                {downPaymentAmount > 0 && (
                  <span className="text-sm font-semibold" style={{ fontFamily: 'var(--font-data)', color: 'var(--color-primary)' }}>
                    {formatCurrency(downPaymentAmount)}
                  </span>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                Ajoutez une ou plusieurs sources (dans l&apos;ordre : refinancement, puis collatéral, puis liquidités en projection).
                {downPaymentSources.length === 1
                  ? ' Une seule ligne : laissez le montant vide pour financer toute la mise de fonds depuis cette source.'
                  : ' Indiquez un montant par ligne ; la somme doit égaler la mise de fonds.'}
              </p>

              {allocationMismatch && (
                <div className="flex items-center gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200">
                  <Icon name="AlertTriangle" size={14} className="text-amber-600 shrink-0" />
                  <p className="text-xs text-amber-800">
                    Somme des montants ({formatCurrency(sumAllocated)}) ≠ mise de fonds ({formatCurrency(downPaymentAmount)}).
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {downPaymentSources.map((row, idx) => {
                  const rowRefiDebts = row.refiAssetId ? getDebtsForAsset(row.refiAssetId) : hypothequeDebts;
                  const rowRefiDebtOpts = rowRefiDebts.map(d => ({ value: d.id, label: `${d.name} (${formatCurrency(d.currentBalance)})` }));
                  const rAsset = allImmobilierAssets.find(a => a.id === row.refiAssetId);
                  const rDebt = debts.find(dd => dd.id === row.refiDebtId);
                  const refiPropertyValueRow = projRow?.assetProjected?.[row.refiAssetId]
                    || parseFloat(row.refiPropertyValue)
                    || (rAsset ? parseFloat(rAsset.currentBalance) : 0);
                  const refiCurrentBalanceRow = projRow?.debtProjected?.[`asset:${row.refiAssetId}`]
                    || projRow?.debtProjected?.[row.refiDebtId]
                    || (rDebt ? parseFloat(rDebt.currentBalance) : 0);
                  const refiNewLTVrow = parseFloat(row.refiNewLTV) || 80;
                  const refiNewBalRow = refiPropertyValueRow * (refiNewLTVrow / 100);
                  const refiCashOutRow = Math.max(0, refiNewBalRow - refiCurrentBalanceRow);
                  const rowWant = parseFloat(row.amount) || 0;
                  const refiShortfallRow = row.type === 'refinancement' && rowWant > 0 && refiCashOutRow + 1 < rowWant
                    ? rowWant - refiCashOutRow
                    : 0;

                  const {
                    vr: colVr,
                    existingDebt: colDebt,
                    maxLoan: colMax,
                    ltvPercent: colLtv,
                  } = getCollateralPreviewForInsuranceAsset(row.collateralAssetId);
                  const colWant = parseFloat(row.amount) || 0;
                  const collateralShortfallRow = row.type === 'collateral_bancaire' && row.collateralAssetId && colWant > 0
                    ? Math.max(0, colWant - colMax)
                    : 0;

                  return (
                    <div key={row.id || idx} className="border border-border rounded-lg p-3 space-y-3 bg-muted/15" data-testid={`down-payment-source-${idx}`}>
                      <div className="flex flex-col md:flex-row md:items-end gap-3">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Select
                            label="Type"
                            value={row.type || 'liquidites'}
                            onChange={(v) => {
                              if (v === 'liquidites') {
                                patchDownPaymentSource(idx, { type: v, liquidSourceId: row.liquidSourceId || '' });
                              } else if (v === 'refinancement') {
                                patchDownPaymentSource(idx, {
                                  type: v,
                                  refiAssetId: row.refiAssetId || '',
                                  refiDebtId: row.refiDebtId || '',
                                  refiPropertyValue: row.refiPropertyValue || '',
                                  refiNewLTV: row.refiNewLTV || '80',
                                  refiNewRate: row.refiNewRate || '',
                                  refiNewAmortization: row.refiNewAmortization || '25',
                                });
                              } else {
                                patchDownPaymentSource(idx, {
                                  type: v,
                                  collateralAssetId: row.collateralAssetId || '',
                                  collateralInterestRate: row.collateralInterestRate || '7.5',
                                  collateralAmortization: row.collateralAmortization || '25',
                                });
                              }
                            }}
                            options={DP_SOURCE_TYPE_OPTIONS}
                          />
                          <Input
                            label="Montant ($)"
                            type="number"
                            value={row.amount || ''}
                            onChange={e => patchDownPaymentSource(idx, { amount: e.target.value })}
                            placeholder={downPaymentSources.length === 1 ? `max ${formatCurrency(downPaymentAmount)}` : ''}
                          />
                        </div>
                        {downPaymentSources.length > 1 && (
                          <Button type="button" size="sm" variant="ghost" iconName="Trash2" className="shrink-0 text-destructive"
                            onClick={() => removeDownPaymentSource(idx)} data-testid={`remove-dp-source-${idx}`} />
                        )}
                      </div>

                      {row.type === 'liquidites' && (
                        <Select
                          label="Compte préféré (optionnel)"
                          value={row.liquidSourceId || ''}
                          onChange={v => patchDownPaymentSource(idx, { liquidSourceId: v })}
                          options={liquidOptions}
                          placeholder={liquidAssets.length === 0 ? 'Aucun compte' : 'Répartition automatique entre comptes'}
                          data-testid={`liquid-source-${idx}`}
                        />
                      )}

                      {row.type === 'refinancement' && (
                        <div className="border-t border-border pt-3 space-y-3" data-testid="refi-for-purchase-section">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon name="RefreshCw" size={14} className="text-primary" />
                            <span className="text-sm font-medium text-foreground">Refinancement</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Select
                              label="Propriété à refinancer"
                              value={row.refiAssetId || ''}
                              onChange={(v) => {
                                const asset = immobilierAssets.find(a => a.id === v);
                                const linked = getDebtsForAsset(v);
                                patchDownPaymentSource(idx, {
                                  refiAssetId: v,
                                  refiPropertyValue: String(asset?.currentBalance || ''),
                                  refiDebtId: linked.length === 1 ? linked[0].id : '',
                                });
                              }}
                              options={immobilierOptions}
                              placeholder={immobilierAssets.length === 0 ? 'Aucune propriété' : 'Sélectionner...'}
                            />
                            <Select
                              label="Hypothèque actuelle"
                              value={row.refiDebtId || ''}
                              onChange={v => patchDownPaymentSource(idx, { refiDebtId: v })}
                              options={rowRefiDebtOpts}
                              placeholder={rowRefiDebtOpts.length === 0 ? 'Aucune hypothèque liée' : 'Sélectionner...'}
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <Input label="Valeur propriété ($)" type="number" value={row.refiPropertyValue || ''}
                              onChange={e => patchDownPaymentSource(idx, { refiPropertyValue: e.target.value })} placeholder="1 200 000" />
                            <Input label="Nouveau prêt-valeur (%)" type="number" value={row.refiNewLTV || ''}
                              onChange={e => patchDownPaymentSource(idx, { refiNewLTV: e.target.value })} placeholder="80" />
                            <Input label="Nouveau taux (%)" type="number" value={row.refiNewRate || ''}
                              onChange={e => patchDownPaymentSource(idx, { refiNewRate: e.target.value })} placeholder="4.5" />
                            <Input label="Amortissement (ans)" type="number" value={row.refiNewAmortization || ''}
                              onChange={e => patchDownPaymentSource(idx, { refiNewAmortization: e.target.value })} placeholder="25" />
                          </div>
                          {row.refiAssetId && (
                            <div className="flex items-center gap-3 p-2.5 rounded-md bg-primary/5 border border-primary/10">
                              <Icon name="Calculator" size={14} className="text-primary" />
                              <div className="text-xs space-y-0.5">
                                {eventYear > 0 && projRow && (
                                  <p className="text-primary/70 font-medium mb-1">Valeurs projetées à l&apos;année {eventYear}</p>
                                )}
                                <p className="text-muted-foreground">
                                  Cash-out estimé: <span className="font-semibold text-primary">{formatCurrency(refiCashOutRow)}</span>
                                  {rowWant > 0 && (
                                    <span className="ml-2">(montant ligne: {formatCurrency(rowWant)})</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          )}
                          {refiShortfallRow > 0 && (
                            <div className="flex items-center gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200">
                              <Icon name="AlertTriangle" size={14} className="text-amber-600" />
                              <p className="text-xs text-amber-800 flex-1">
                                Cash-out ({formatCurrency(refiCashOutRow)}) inférieur au montant demandé ({formatCurrency(rowWant)}). Ajoutez une source liquidités ou réduisez le montant.
                              </p>
                              <button
                                type="button"
                                className="text-xs font-semibold text-primary underline whitespace-nowrap"
                                onClick={() => {
                                  setForm(prev => ({
                                    ...prev,
                                    details: {
                                      ...prev.details,
                                      downPaymentSources: [
                                        ...(prev.details.downPaymentSources || downPaymentSources),
                                        { id: newDpSourceId(), type: 'liquidites', amount: String(Math.round(refiShortfallRow)), liquidSourceId: '' },
                                      ],
                                    },
                                  }));
                                }}
                              >
                                + Liquidités
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {row.type === 'collateral_bancaire' && (
                        <div className="border-t border-border pt-3 space-y-3" data-testid="collateral-for-purchase-section">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon name="Shield" size={14} className="text-primary" />
                            <span className="text-sm font-medium text-foreground">Prêt collatéral (assurance participative)</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Select
                              label="Police en garantie"
                              value={row.collateralAssetId || ''}
                              onChange={(v) => {
                                const { maxLoan } = getCollateralPreviewForInsuranceAsset(v);
                                const cap = Math.min(downPaymentAmount || 0, maxLoan);
                                patchDownPaymentSource(idx, {
                                  collateralAssetId: v,
                                  amount: row.amount || String(Math.round(cap) || ''),
                                });
                              }}
                              options={insuranceOptions}
                              placeholder={insuranceOptions.length === 0 ? 'Aucune assurance' : 'Sélectionner...'}
                            />
                            <Input
                              label="Taux d'intérêt (%)"
                              type="number"
                              value={row.collateralInterestRate || ''}
                              onChange={e => patchDownPaymentSource(idx, { collateralInterestRate: e.target.value })}
                              placeholder="7.5"
                            />
                          </div>
                          <Input
                            label="Amortissement (ans)"
                            type="number"
                            value={row.collateralAmortization || ''}
                            onChange={e => patchDownPaymentSource(idx, { collateralAmortization: e.target.value })}
                            placeholder="25"
                          />
                          {row.collateralAssetId && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-3 p-2.5 rounded-md bg-primary/5 border border-primary/10">
                                <Icon name="Calculator" size={14} className="text-primary" />
                                <div className="text-xs space-y-0.5">
                                  <p className="text-primary/70 font-medium">À l&apos;année {eventYear}</p>
                                  <p className="text-muted-foreground">
                                    VR: {formatCurrency(colVr)} × {colLtv || DEFAULT_COLLATERAL_LTV}% → {formatCurrency(colMax + colDebt)}
                                    {' — Encours: '}{formatCurrency(colDebt)}
                                    {' → Disp.: '}
                                    <span className="font-semibold text-primary">{formatCurrency(colMax)}</span>
                                  </p>
                                  <p className="text-muted-foreground mt-1">Prêt collatéral externe (≠ avance sur police, non imposable à l&apos;emprunt).</p>
                                </div>
                              </div>
                              {collateralShortfallRow > 0 && (
                                <div className="flex items-center gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200">
                                  <Icon name="AlertTriangle" size={14} className="text-amber-600" />
                                  <p className="text-xs text-amber-800 flex-1">
                                    Capacité ({formatCurrency(colMax)}) inférieure au montant ligne ({formatCurrency(colWant)}). Manque ~{formatCurrency(collateralShortfallRow)}.
                                  </p>
                                  <button
                                    type="button"
                                    className="text-xs font-semibold text-primary underline whitespace-nowrap"
                                    onClick={() => {
                                      setForm(prev => ({
                                        ...prev,
                                        details: {
                                          ...prev.details,
                                          downPaymentSources: [
                                            ...(prev.details.downPaymentSources || downPaymentSources),
                                            { id: newDpSourceId(), type: 'liquidites', amount: String(Math.round(collateralShortfallRow)), liquidSourceId: '' },
                                          ],
                                        },
                                      }));
                                    }}
                                  >
                                    + Liquidités
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <Button type="button" size="sm" variant="outline" iconName="Plus" onClick={addDownPaymentSource} data-testid="add-down-payment-source">
                Ajouter une source
              </Button>
            </div>
          </>
        );
      }
      case 'vente': {
        const linkedDebts = form.details?.assetId ? getDebtsForAsset(form.details.assetId) : hypothequeDebts;
        const debtOpts = linkedDebts.map(d => ({ value: d.id, label: `${d.name} (${formatCurrency(d.currentBalance)})` }));
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select label="Propriété à vendre" value={form.details.assetId || ''} onChange={handleSelectAssetForSale}
                options={immobilierOptions} placeholder="Sélectionner..." required data-testid="event-sale-asset" />
              <Select label="Hypothèque associée" value={form.details.debtId || ''} onChange={v => setDetail('debtId', v)}
                options={debtOpts} placeholder="Aucune" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="Prix de vente ($)" type="number" value={form.details.salePrice || ''} onChange={e => setDetail('salePrice', e.target.value)} placeholder="1 300 000" data-testid="event-sale-price" />
              <Input label="Coût de base ajusté — ACB ($)" type="number" value={form.details.acb || ''} onChange={e => setDetail('acb', e.target.value)} placeholder="800 000" data-testid="event-sale-acb" />
              <Select label="Détenteur" value={form.details.entityId || ''} onChange={v => setDetail('entityId', v)} options={entityOptions} placeholder="Sélectionner..." />
            </div>
            {form.details.salePrice && form.details.acb && (
              <div className="flex items-center gap-3 p-2.5 rounded-md bg-primary/5 border border-primary/10" data-testid="sale-gain-preview">
                <Icon name="Calculator" size={14} className="text-primary" />
                <div className="text-xs space-y-0.5">
                  <p className="text-muted-foreground">
                    Gain en capital: <span className="font-semibold text-foreground">{formatCurrency(Math.max(0, (parseFloat(form.details.salePrice) || 0) - (parseFloat(form.details.acb) || 0)))}</span>
                    {' '}(inclusion 50%: <span className="font-semibold text-warning">{formatCurrency(Math.max(0, (parseFloat(form.details.salePrice) || 0) - (parseFloat(form.details.acb) || 0)) * 0.5)}</span>)
                  </p>
                </div>
              </div>
            )}

            {/* Fund destination for sale proceeds */}
            <div className="border border-border rounded-lg p-4 space-y-3" data-testid="sale-fund-destination">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="ArrowRightLeft" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Destination des fonds de vente</span>
              </div>
              <Select
                label="Diriger les fonds vers"
                value={form.details.fundDestinationId || '__new__'}
                onChange={v => setDetail('fundDestinationId', v)}
                options={fundDestinationOptions}
                data-testid="sale-fund-destination-select"
              />
              {(form.details.fundDestinationId === '__new__' || !form.details.fundDestinationId) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    label="Nom du nouveau compte"
                    value={form.details.newAccountName || ''}
                    onChange={e => setDetail('newAccountName', e.target.value)}
                    placeholder="Ex: Compte placement vente"
                    data-testid="sale-new-account-name"
                  />
                  <Select
                    label="Type de compte"
                    value={form.details.newAccountType || 'non_enregistre'}
                    onChange={v => setDetail('newAccountType', v)}
                    options={FUND_ACCOUNT_TYPES}
                    data-testid="sale-new-account-type"
                  />
                </div>
              )}
            </div>
          </>
        );
      }
      case 'renovation': {
        const RENO_FUNDING_SOURCES = [
          { value: 'liquidites', label: 'Liquidités / Épargne' },
          { value: 'marge_credit', label: 'Marge de crédit / Emprunt' },
          { value: 'refinancement', label: 'Refinancement hypothécaire' },
        ];
        const liquidAssetsForReno = assets.filter(a => a.type !== 'immobilier' && a.type !== 'assurance_participative' && (parseFloat(a.currentBalance) || 0) > 0);
        const liquidOptionsForReno = liquidAssetsForReno.map(a => ({ value: a.id, label: `${a.name} (${formatCurrency(a.currentBalance)})` }));
        const renoRefiDebts = form.details?.renoRefiAssetId ? getDebtsForAsset(form.details.renoRefiAssetId) : hypothequeDebts;
        const renoRefiDebtOpts = renoRefiDebts.map(d => ({ value: d.id, label: `${d.name} (${formatCurrency(d.currentBalance)})` }));

        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select label="Immeuble à rénover" value={form.details.assetId || ''} onChange={v => {
                const asset = immobilierAssets.find(a => a.id === v);
                setForm(prev => ({
                  ...prev,
                  name: prev.name || `Rénovation - ${asset?.name || ''}`,
                  details: { ...prev.details, assetId: v },
                }));
              }} options={immobilierOptions} placeholder="Sélectionner..." required data-testid="event-reno-asset" />
              <Select label="Détenteur" value={form.details.entityId || ''} onChange={v => setDetail('entityId', v)} options={entityOptions} placeholder="Sélectionner..." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label="Coût de la rénovation ($)" type="number" value={form.details.renovationCost || ''} onChange={e => setDetail('renovationCost', e.target.value)} placeholder="50 000" data-testid="event-reno-cost" />
              <Input label="Hausse valeur de l'actif (%)" type="number" value={form.details.valueIncreasePercent || ''} onChange={e => setDetail('valueIncreasePercent', e.target.value)} placeholder="10" data-testid="event-reno-value-increase" />
              <Input label="Hausse des revenus (%)" type="number" value={form.details.revenueIncreasePercent || ''} onChange={e => setDetail('revenueIncreasePercent', e.target.value)} placeholder="15" data-testid="event-reno-revenue-increase" />
            </div>

            {/* Funding source */}
            <div className="border border-border rounded-lg p-4 space-y-3" data-testid="reno-funding-source">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Wallet" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Source de financement</span>
              </div>
              <Select
                label="Provenance des fonds"
                value={form.details.fundingSource || 'liquidites'}
                onChange={v => setDetail('fundingSource', v)}
                options={RENO_FUNDING_SOURCES}
                data-testid="reno-funding-source-select"
              />
              {(form.details.fundingSource === 'liquidites' || !form.details.fundingSource) && (
                <Select
                  label="Compte source"
                  value={form.details.fundingAccountId || ''}
                  onChange={v => setDetail('fundingAccountId', v)}
                  options={liquidOptionsForReno}
                  placeholder={liquidAssetsForReno.length === 0 ? 'Aucun compte disponible' : 'Sélectionner...'}
                  data-testid="reno-funding-account"
                />
              )}
              {form.details.fundingSource === 'refinancement' && (
                <div className="space-y-3 border-t border-border pt-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Select label="Propriété à refinancer" value={form.details.renoRefiAssetId || ''} onChange={v => {
                      const a = immobilierAssets.find(x => x.id === v);
                      const linked = getDebtsForAsset(v);
                      setForm(prev => ({ ...prev, details: { ...prev.details, renoRefiAssetId: v, renoRefiPropertyValue: String(a?.currentBalance || ''), renoRefiDebtId: linked.length === 1 ? linked[0].id : '' } }));
                    }} options={immobilierOptions} placeholder="Sélectionner..." data-testid="reno-refi-asset" />
                    <Select label="Hypothèque actuelle" value={form.details.renoRefiDebtId || ''} onChange={v => setDetail('renoRefiDebtId', v)} options={renoRefiDebtOpts} placeholder="Sélectionner..." data-testid="reno-refi-debt" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input label="Valeur propriété ($)" type="number" value={form.details.renoRefiPropertyValue || ''} onChange={e => setDetail('renoRefiPropertyValue', e.target.value)} placeholder="1 200 000" />
                    <Input label="Nouveau ratio LTV (%)" type="number" value={form.details.renoRefiLTV || ''} onChange={e => setDetail('renoRefiLTV', e.target.value)} placeholder="80" />
                    <Input label="Nouveau taux (%)" type="number" value={form.details.renoRefiRate || ''} onChange={e => setDetail('renoRefiRate', e.target.value)} placeholder="4.5" />
                  </div>
                </div>
              )}
            </div>
          </>
        );
      }
      case 'produit_financier':
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Nom du produit" value={form.details.productName || ''} onChange={e => setDetail('productName', e.target.value)} placeholder="Assurance participative" />
            <Input label="Montant initial ($)" type="number" value={form.details.initialAmount || ''} onChange={e => setDetail('initialAmount', e.target.value)} placeholder="50 000" />
            <Input label="Cotisation mensuelle ($)" type="number" value={form.details.monthlyContribution || ''} onChange={e => setDetail('monthlyContribution', e.target.value)} placeholder="500" />
            <Input label="Rendement attendu (%)" type="number" value={form.details.expectedReturn || ''} onChange={e => setDetail('expectedReturn', e.target.value)} placeholder="5" />
            <Select label="Type de compte" value={form.details.productType || 'autre'} onChange={v => setDetail('productType', v)} options={[
              { value: 'reer', label: 'REER' }, { value: 'celi', label: 'CELI' },
              { value: 'non_enregistre', label: 'Non-enregistré' }, { value: 'corporatif', label: 'Corporatif' }, { value: 'autre', label: 'Autre' },
            ]} />
            <Select label="Détenteur" value={form.details.entityId || ''} onChange={v => setDetail('entityId', v)} options={entityOptions} placeholder="Sélectionner..." />
          </div>
        );
      case 'assurance_participative': {
        const scheduleRows = parsedInsurance?.length ? parsedInsurance : (form.details?.insuranceSchedule || []);
        const lastRow = scheduleRows[scheduleRows.length - 1];
        const currentIdx = scheduleRows.findIndex(d => d.year === currentPolicyYear);
        const currentRow = currentIdx >= 0 ? scheduleRows[currentIdx] : null;
        const hasSchedule = scheduleRows.length > 0;

        return (
          <>
            <div className="border border-border rounded-lg p-4 space-y-3" data-testid="event-insurance-import">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Shield" size={16} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Import de la police d'assurance</span>
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer p-2.5 rounded-lg border border-border bg-muted/30 mb-1" data-testid="event-insurance-premiums-cashflow-toggle">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input accent-primary mt-0.5"
                  checked={form.details?.includePremiumsInCashflow !== false}
                  onChange={e => setDetail('includePremiumsInCashflow', e.target.checked)}
                />
                <span>
                  <span className="text-sm font-medium text-foreground">Inclure les primes dans le cashflow</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Si coché, la prime annuelle réduit le cashflow projeté (dépense). Sinon, projection de la valeur de rachat seulement.
                  </span>
                </span>
              </label>
              <p className="text-xs text-muted-foreground">
                Importez un fichier XLSX ou CSV avec les valeurs projetées de votre police.
              </p>

              <input
                ref={insuranceFileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleInsuranceFile}
                className="text-sm text-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-border file:text-sm file:font-medium file:bg-card file:text-foreground file:cursor-pointer hover:file:bg-muted"
                data-testid="event-insurance-file-input"
              />

              {insuranceError && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                  <Icon name="AlertCircle" size={16} />
                  {insuranceError}
                </div>
              )}

              {hasSchedule && (
                <>
                  <div className="border border-border rounded-lg p-3 space-y-3" data-testid="event-insurance-preview">
                    <div className="flex items-center gap-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <span className="text-sm font-medium text-foreground">
                        {scheduleRows.length} années de données trouvées
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="bg-card rounded-md p-2.5 border border-border">
                        <p className="text-xs text-muted-foreground">Valeur de rachat (dernière année)</p>
                        <p className="text-sm font-semibold text-primary" style={{ fontFamily: 'var(--font-data)' }}>
                          {formatCurrency(lastRow?.surrenderValue || 0)}
                        </p>
                      </div>
                      <div className="bg-card rounded-md p-2.5 border border-border">
                        <p className="text-xs text-muted-foreground">Assurance vie totale</p>
                        <p className="text-sm font-semibold" style={{ fontFamily: 'var(--font-data)', color: 'var(--color-success)' }}>
                          {formatCurrency(lastRow?.totalInsurance || 0)}
                        </p>
                      </div>
                      <div className="bg-card rounded-md p-2.5 border border-border">
                        <p className="text-xs text-muted-foreground">Primes cumulatives</p>
                        <p className="text-sm font-semibold text-foreground" style={{ fontFamily: 'var(--font-data)' }}>
                          {formatCurrency(scheduleRows.reduce((s, d) => s + (d.premiumPaid || 0), 0))}
                        </p>
                      </div>
                    </div>

                    {/* Data preview table */}
                    <div className="max-h-[140px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-muted">
                          <tr className="text-left text-muted-foreground border-b border-border">
                            <th className="pb-1.5 pr-2">Année</th>
                            <th className="pb-1.5 pr-2">Âge</th>
                            <th className="pb-1.5 pr-2 text-right">Valeur de rachat</th>
                            <th className="pb-1.5 pr-2 text-right">Assurance vie</th>
                            <th className="pb-1.5 text-right">Prime</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {scheduleRows.slice(0, 6).map((row, i) => (
                            <tr key={i} className={row.year === currentPolicyYear ? 'bg-primary/10' : ''}>
                              <td className="py-1 pr-2">{row.year}{row.year === currentPolicyYear ? ' *' : ''}</td>
                              <td className="py-1 pr-2 text-muted-foreground">{row.age || '—'}</td>
                              <td className="py-1 pr-2 text-right" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(row.surrenderValue)}</td>
                              <td className="py-1 pr-2 text-right" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(row.totalInsurance)}</td>
                              <td className="py-1 text-right" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(row.premiumPaid)}</td>
                            </tr>
                          ))}
                          {scheduleRows.length > 6 && (
                            <tr><td colSpan={5} className="py-1 text-center text-muted-foreground">... {scheduleRows.length - 6} lignes supplémentaires</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Current year selection */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Année courante de la police</label>
                    <select
                      value={currentPolicyYear}
                      onChange={e => setCurrentPolicyYear(parseInt(e.target.value, 10))}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                      data-testid="event-insurance-year-select"
                    >
                      {scheduleRows.map(row => (
                        <option key={row.year} value={row.year}>
                          Année {row.year}{row.age ? ` (âge ${row.age})` : ''} — Rachat: {formatCurrency(row.surrenderValue)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">Où en êtes-vous dans votre police ?</p>
                  </div>

                  {currentRow && (
                    <div className="flex items-center gap-2 p-2.5 rounded-md bg-primary/5 border border-primary/10 text-xs">
                      <Icon name="Info" size={14} className="text-primary shrink-0" />
                      <span className="text-foreground">
                        Année courante ({currentPolicyYear}): Valeur de rachat = <strong>{formatCurrency(currentRow.surrenderValue)}</strong>, Capital-décès = <strong>{formatCurrency(currentRow.totalInsurance)}</strong>
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            <Select label="Détenteur" value={form.details.entityId || ''} onChange={v => setDetail('entityId', v)} options={entityOptions} placeholder="Sélectionner..." />
            {form.details?.includePremiumsInCashflow !== false && hasSchedule && (
              <Select
                label="Compte de prélèvement des primes"
                value={form.details?.premiumDebitAssetId || ''}
                onChange={v => setDetail('premiumDebitAssetId', v)}
                options={premiumDebitSelectOptions}
                placeholder={premiumDebitSelectOptions.length === 0 ? 'Ajoutez un compte liquide au bilan' : 'Compte débité pour la prime...'}
                required
                data-testid="event-insurance-premium-debit"
              />
            )}
          </>
        );
      }
      case 'plan_financement_immediat': {
        const policyOptions = insuranceAssets.map((a) => ({
          value: a.id,
          label: `${a.name} (${formatCurrency(a.currentBalance)})`,
        }));
        return (
          <div className="space-y-3 border border-border rounded-lg p-4" data-testid="event-pfi-form">
            <p className="text-xs text-muted-foreground">
              Active le plan de financement immédiat (PFI) sur une police au bilan : avances annuelles de prêt collatéral jusqu&apos;à la capacité (LTV), intérêts et frais de garantie optionnels. Estimation — valider avec fiscaliste.
            </p>
            <Select
              label="Police d'assurance"
              value={form.details.policyAssetId || ''}
              onChange={(v) => setDetail('policyAssetId', v)}
              options={policyOptions}
              placeholder="Sélectionner la police..."
              required
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Années d'avances" type="number" value={form.details.pfiAdvanceYears ?? '10'} onChange={(e) => setDetail('pfiAdvanceYears', e.target.value)} />
              <Input label="Taux d'intérêt (%)" type="number" value={form.details.pfiInterestRate ?? '7.5'} onChange={(e) => setDetail('pfiInterestRate', e.target.value)} />
              <Input label="LTV collatéral (%)" type="number" value={form.details.collateralLtvPercent ?? String(DEFAULT_COLLATERAL_LTV)} onChange={(e) => setDetail('collateralLtvPercent', e.target.value)} />
              <Input label="Frais garantie (% / an)" type="number" value={form.details.guaranteeFeePercent ?? '1.5'} onChange={(e) => setDetail('guaranteeFeePercent', e.target.value)} placeholder="Si emprunteur = actionnaire" />
              <Select label="Emprunteur" value={form.details.pfiBorrower || 'corporation'} onChange={(v) => setDetail('pfiBorrower', v)} options={[{ value: 'corporation', label: 'Société (titulaire)' }, { value: 'shareholder', label: 'Actionnaire' }]} />
              <Input label="Avance max. / an ($)" type="number" value={form.details.pfiAnnualAdvanceAmount ?? ''} onChange={(e) => setDetail('pfiAnnualAdvanceAmount', e.target.value)} placeholder="Vide = capacité disponible" />
            </div>
          </div>
        );
      }
      case 'declaration_dividende': {
        const corpOptions = entities.filter((e) => e.type === 'corporation').map((e) => ({ value: e.id, label: e.name }));
        const divClassOptions = [
          { value: 'capital', label: 'Capital (CDC — non imposable)' },
          { value: 'eligible', label: 'Admissible (GRIP)' },
          { value: 'non_eligible', label: 'Ordinaire (IMRTD)' },
        ];
        return (
          <div className="space-y-3 border border-border rounded-lg p-4" data-testid="event-dividend-declaration-form">
            <p className="text-xs text-muted-foreground">
              Versement de dividendes de la société vers les actionnaires selon leurs parts.
            </p>
            <Select label="Société (Inc.)" value={form.details.corpEntityId || ''} onChange={(v) => setDetail('corpEntityId', v)} options={corpOptions} required />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select label="Classe de dividende" value={form.details.dividendClass || 'non_eligible'} onChange={(v) => setDetail('dividendClass', v)} options={divClassOptions} />
              <Input label="Montant total ($)" type="number" value={form.details.totalAmount ?? ''} onChange={(e) => setDetail('totalAmount', e.target.value)} required />
            </div>
          </div>
        );
      }
      case 'avance_sur_police': {
        const policyOptions = insuranceAssets.map((a) => ({
          value: a.id,
          label: `${a.name} (${formatCurrency(a.currentBalance)})`,
        }));
        return (
          <div className="space-y-3 border border-dashed border-amber-300 rounded-lg p-4 bg-amber-50/50" data-testid="event-policy-advance-form">
            <p className="text-xs text-amber-900">
              Avance sur police = disposition réputée (revenu imposable ≈ avance − CBR). Distinct du prêt collatéral bancaire.
            </p>
            <Select label="Police" value={form.details.policyAssetId || ''} onChange={(v) => setDetail('policyAssetId', v)} options={policyOptions} required />
            <Input label="Montant de l'avance ($)" type="number" value={form.details.amount ?? ''} onChange={(e) => setDetail('amount', e.target.value)} required />
          </div>
        );
      }
      case 'changement_contributions': {
        const changes = Array.isArray(form.details?.changes) && form.details.changes.length > 0
          ? form.details.changes
          : [{ assetId: '', monthlyContribution: '', avgAnnualReturn: '' }];
        const liquidOpts = assets
          .filter(a => a.type !== 'immobilier' && a.type !== 'assurance_participative')
          .map(a => ({ value: a.id, label: `${a.name} — ${entities.find(e => e.id === a.entityId)?.name || ''}` }));

        const patchChangeRow = (idx, patch) => {
          setForm(prev => {
            const base = prev.details?.changes?.length ? [...prev.details.changes] : [{ assetId: '', monthlyContribution: '', avgAnnualReturn: '' }];
            base[idx] = { ...base[idx], ...patch };
            return { ...prev, details: { ...prev.details, changes: base } };
          });
        };
        const addChangeRow = () => {
          setForm(prev => ({
            ...prev,
            details: {
              ...prev.details,
              changes: [...(prev.details?.changes || [{ assetId: '', monthlyContribution: '', avgAnnualReturn: '' }]), { assetId: '', monthlyContribution: '', avgAnnualReturn: '' }],
            },
          }));
        };
        const removeChangeRow = (idx) => {
          setForm(prev => {
            const cur = prev.details?.changes || [];
            const next = cur.filter((_, i) => i !== idx);
            return { ...prev, details: { ...prev.details, changes: next.length ? next : [{ assetId: '', monthlyContribution: '', avgAnnualReturn: '' }] } };
          });
        };

        return (
          <div className="space-y-3 border border-border rounded-lg p-4" data-testid="event-contribution-changes">
            <p className="text-xs text-muted-foreground">
              Ajustez la cotisation mensuelle et/ou le rendement annuel (%) des comptes liquides à partir de cette année.
              Laissez un champ vide pour ne pas modifier cette valeur.
            </p>
            {changes.map((row, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border-b border-border pb-3 last:border-0">
                <div className="md:col-span-5">
                  <Select
                    label="Compte"
                    value={row.assetId || ''}
                    onChange={v => patchChangeRow(idx, { assetId: v })}
                    options={liquidOpts}
                    placeholder="Sélectionner..."
                  />
                </div>
                <div className="md:col-span-3">
                  <Input
                    label="Cotisation mensuelle ($)"
                    type="number"
                    value={row.monthlyContribution ?? ''}
                    onChange={e => patchChangeRow(idx, { monthlyContribution: e.target.value })}
                    placeholder="vide = inchangé"
                  />
                </div>
                <div className="md:col-span-3">
                  <Input
                    label="Rendement annuel (%)"
                    type="number"
                    value={row.avgAnnualReturn ?? ''}
                    onChange={e => patchChangeRow(idx, { avgAnnualReturn: e.target.value })}
                    placeholder="vide = inchangé"
                  />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <Button type="button" size="sm" variant="ghost" iconName="Trash2" onClick={() => removeChangeRow(idx)} disabled={changes.length <= 1} aria-label="Retirer" />
                </div>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" iconName="Plus" onClick={addChangeRow}>
              Ajouter un compte
            </Button>
          </div>
        );
      }
      case 'cashflow_direction': {
        const entitiesById = {};
        entities.forEach(e => { entitiesById[e.id] = e; });

        const routingBySource = form.details?.routingBySource || {};
        const setSourceRoute = (sourceId, route) => {
          setForm(prev => ({
            ...prev,
            details: {
              ...(prev.details || {}),
              routingBySource: {
                ...(prev.details?.routingBySource || {}),
                [sourceId]: route,
              },
            },
          }));
        };

        const getSourceRoute = (source) => {
          const current = routingBySource?.[source.id];
          if (current) return current;
          return {
            dividendTaxMode: source.type === 'corporation' ? 'corporatif' : 'personnel',
            destinations: [],
          };
        };

        const holdingSelectOptions = [
          { value: 'corporatif', label: 'Avec impôt sur dividendes' },
          { value: 'personnel', label: 'Sans impôt sur dividendes' },
        ];

        if (!entities || entities.length === 0) {
          return (
            <div className="text-sm text-muted-foreground">
              Ajoutez des détenteurs dans l’onglet <span className="font-medium">Bilan</span> pour configurer la direction du cashflow.
            </div>
          );
        }

        const accountOptions = (() => {
          const eligible = (assets || []).filter(a => a.type !== 'immobilier');
          return [
            { value: '', label: 'Sélectionner...' },
            ...eligible.map(a => {
              const ent = entitiesById[a.entityId];
              const entLabel = ent ? `${ent.name} (${ent.type === 'personnel' ? 'Personnel' : 'Corporation'})` : 'Sans détenteur';
              return { value: a.id, label: `${a.name} — ${entLabel}` };
            }),
          ];
        })();

        return (
          <div className="space-y-4">
            {entities.map(source => {
              const route = getSourceRoute(source);
              const destinations = route?.destinations || [];
              const hasPersonalDest = destinations.some(d => {
                const a = assets.find(x => x.id === d.assetId);
                const ent = a ? entitiesById[a.entityId] : null;
                return source.type === 'corporation' && ent?.type === 'personnel';
              });

              return (
                <div key={source.id} className="border border-border rounded-lg p-4 bg-muted/30" data-testid={`cashflow-direction-route-${source.id}`}>
                  <p className="text-sm font-semibold text-foreground">
                    Source: {source.name}
                    <span className="text-xs text-muted-foreground ml-2">({source.type === 'personnel' ? 'Personnel' : 'Corporation'})</span>
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <Select
                      label="Dividendes si transfert corporatif → personnel"
                      value={route.dividendTaxMode || 'corporatif'}
                      onChange={(v) => setSourceRoute(source.id, { ...route, dividendTaxMode: v })}
                      options={holdingSelectOptions}
                      disabled={!hasPersonalDest}
                      data-testid={`cashflow-direction-divmode-${source.id}`}
                    />
                  </div>

                  <div className="space-y-2 mt-3">
                    {destinations.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Aucun compte sélectionné.</p>
                    ) : (
                      destinations.map((d, idx) => (
                        <div key={`${source.id}-${idx}`} className="grid grid-cols-1 md:grid-cols-[1fr_140px_40px] gap-2 items-end">
                          <Select
                            label={idx === 0 ? 'Compte' : ''}
                            value={d.assetId || ''}
                            onChange={(v) => {
                              const next = [...destinations];
                              next[idx] = { ...(next[idx] || {}), assetId: v };
                              setSourceRoute(source.id, { ...route, destinations: next });
                            }}
                            options={accountOptions}
                            data-testid={`cashflow-direction-account-${source.id}-${idx}`}
                          />
                          <Input
                            label={idx === 0 ? 'Répartition (%)' : ''}
                            type="number"
                            value={d.percent ?? 0}
                            onChange={(e) => {
                              const next = [...destinations];
                              next[idx] = { ...(next[idx] || {}), percent: e.target.value };
                              setSourceRoute(source.id, { ...route, destinations: next });
                            }}
                            data-testid={`cashflow-direction-percent-${source.id}-${idx}`}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            iconName="Trash2"
                            onClick={() => {
                              const next = [...destinations];
                              next.splice(idx, 1);
                              setSourceRoute(source.id, { ...route, destinations: next });
                            }}
                            style={{ color: 'var(--color-destructive)' }}
                            data-testid={`cashflow-direction-remove-${source.id}-${idx}`}
                          />
                        </div>
                      ))
                    )}

                    <div>
                      <Button
                        size="sm"
                        variant="outline"
                        iconName="Plus"
                        iconPosition="left"
                        onClick={() => {
                          const next = [...destinations];
                          next.push({ assetId: '', percent: next.length === 0 ? 100 : 0 });
                          setSourceRoute(source.id, { ...route, destinations: next });
                        }}
                        data-testid={`cashflow-direction-add-${source.id}`}
                      >
                        Ajouter un compte
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
      default:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Impact sur actifs ($)" type="number" value={form.details.assetImpact || ''} onChange={e => setDetail('assetImpact', e.target.value)} />
            <Input label="Impact sur dettes ($)" type="number" value={form.details.debtImpact || ''} onChange={e => setDetail('debtImpact', e.target.value)} />
          </div>
        );
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid="planning-events">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Icon name="Calendar" size={20} className="text-primary" />
            Événements planifiés
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Planifiez refinancements, achats, ventes et produits financiers</p>
        </div>
        <Button size="sm" iconName="Plus" iconPosition="left" onClick={openForm} data-testid="add-event-btn">Ajouter</Button>
      </div>

      {showForm && (
        <div className="bg-muted rounded-lg p-4 mb-4 space-y-3" data-testid="event-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Nom" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Refinancement 799 boul Bois franc" required />
            <Select label="Type" value={form.type} onChange={v => {
              if (v === 'achat') {
                setForm({
                  ...form,
                  type: v,
                  details: { downPaymentSources: [{ id: newDpSourceId(), type: 'liquidites', amount: '', liquidSourceId: '' }] },
                });
              } else if (v === 'assurance_participative') {
                setForm({ ...form, type: v, details: { includePremiumsInCashflow: true, premiumDebitAssetId: '' } });
              } else if (v === 'changement_contributions') {
                setForm({ ...form, type: v, details: { changes: [{ assetId: '', monthlyContribution: '', avgAnnualReturn: '' }] } });
              } else if (v === 'plan_financement_immediat') {
                setForm({ ...form, type: v, details: { pfiAdvanceYears: 10, pfiInterestRate: 7.5, collateralLtvPercent: DEFAULT_COLLATERAL_LTV, guaranteeFeePercent: 1.5, pfiBorrower: 'corporation' } });
              } else if (v === 'avance_sur_police') {
                setForm({ ...form, type: v, details: { amount: '' } });
              } else if (v === 'declaration_dividende') {
                setForm({ ...form, type: v, details: { corpEntityId: '', dividendClass: 'non_eligible', totalAmount: '' } });
              } else {
                setForm({ ...form, type: v, details: {} });
              }
            }} options={EVENT_TYPES} />
            <Select label="Année" value={form.year} onChange={v => setForm({ ...form, year: v })} options={yearOptions} />
          </div>
          {renderDetailFields()}
          <Input label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optionnel" />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} data-testid="event-save-btn">{editingId ? 'Mettre à jour' : 'Ajouter'}</Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>Annuler</Button>
          </div>
        </div>
      )}

      {sortedEvents.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="CalendarPlus" size={28} className="text-primary" />
          </div>
          <p className="text-sm text-muted-foreground mb-1">Aucun événement planifié</p>
          <p className="text-xs text-muted-foreground">Ajoutez des événements pour voir leur impact sur votre projection</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 p-3" data-testid="events-year-track">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Milestone" size={14} className="text-primary" />
              <p className="text-xs text-muted-foreground">Événements à venir</p>
            </div>
            <div className="overflow-x-auto pb-1">
              <div className="relative min-w-[720px] px-1">
                <div className="absolute left-0 right-0 top-8 h-0.5 bg-border" />
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.max(1, timelineYears.length)}, minmax(80px, 1fr))` }}>
                  {timelineYears.map((year) => {
                    const eventsForYear = sortedEvents.filter(e => (parseInt(e.year, 10) || 0) === year);
                    const isDropActive = draggedEventId !== null;
                    return (
                      <button
                        key={year}
                        type="button"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const id = e.dataTransfer.getData('text/plain') || draggedEventId;
                          if (id) moveEventToYear(id, year);
                          setDraggedEventId(null);
                        }}
                        className={`relative text-center rounded-md p-1 transition-colors ${isDropActive ? 'bg-primary/5' : ''}`}
                      >
                        <p className="text-sm font-semibold text-foreground">{year}</p>
                        <div className="mx-auto mt-2 h-3 w-3 rounded-full border-2 border-card bg-primary" />
                        <div className="mt-3 space-y-1">
                          {eventsForYear.slice(0, 2).map((ev) => (
                            <div key={ev.id} className="mx-auto w-fit rounded-sm border border-border bg-card px-2 py-0.5 text-[11px] text-foreground">
                              {getTypeLabel(ev.type)}
                            </div>
                          ))}
                          {eventsForYear.length > 2 && (
                            <p className="text-[10px] text-muted-foreground">+{eventsForYear.length - 2}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {timelineYears.length === 0 && (
              <p className="text-xs text-muted-foreground">Ajoutez un événement pour générer l’échelle de temps.</p>
            )}
          </div>

        <div className="relative pl-6 space-y-4" data-testid="events-timeline">
          <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-border" />
          {sortedEvents.map(event => {
            const isDisabled = event.details?.disabled === true;
            return (
            <div
              key={event.id}
              draggable
              onDragStart={(e) => {
                setDraggedEventId(String(event.id));
                e.dataTransfer.setData('text/plain', String(event.id));
              }}
              onDragEnd={() => setDraggedEventId(null)}
              className={`relative transition-opacity ${isDisabled ? 'opacity-50' : ''}`}
              data-testid={`event-item-${event.id}`}
            >
              <div className="absolute -left-6 top-3 w-5 h-5 rounded-full border-2 border-card flex items-center justify-center" style={{ background: isDisabled ? '#94a3b8' : getTypeColor(event.type) }}>
                <Icon name={getTypeIcon(event.type)} size={10} style={{ color: 'white' }} />
              </div>
              <div className={`border rounded-lg p-3 transition-colors ${isDisabled ? 'bg-muted/30 border-border/50' : 'bg-muted/50 border-border hover:bg-muted/80'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    <label className="relative flex items-center cursor-pointer mt-0.5 shrink-0" data-testid={`event-toggle-${event.id}`}>
                      <input
                        type="checkbox"
                        checked={!isDisabled}
                        onChange={() => handleToggleEvent(event.id)}
                        className="sr-only peer"
                      />
                      <div className="w-8 h-[18px] bg-gray-300 peer-checked:bg-emerald-500 rounded-full transition-colors relative">
                        <div className="absolute top-[2px] left-[2px] w-[14px] h-[14px] bg-white rounded-full shadow transition-transform peer-checked:translate-x-[14px]" style={{ transform: !isDisabled ? 'translateX(14px)' : 'translateX(0)' }} />
                      </div>
                    </label>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${getTypeColor(event.type)}20`, color: isDisabled ? '#94a3b8' : getTypeColor(event.type) }}>{event.year}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{getTypeLabel(event.type)}</span>
                        {isDisabled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 font-medium">Désactivé</span>}
                      </div>
                      <p className={`text-sm font-medium mt-1 ${isDisabled ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{event.name}</p>
                      {event.notes && <p className="text-xs text-muted-foreground mt-0.5">{event.notes}</p>}
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {event.type === 'achat' && event.details?.purchasePrice && <span className="text-xs text-muted-foreground">Prix: {formatCurrency(event.details.purchasePrice)}</span>}
                      {event.type === 'achat' && (() => {
                        const dp = (parseFloat(event.details?.purchasePrice) || 0) * ((parseFloat(event.details?.downPaymentPercent) || 20) / 100);
                        const srcs = getAchatDownPaymentSources(event.details || {}, dp);
                        const parts = srcs.map((s) => {
                          if (s.type === 'liquidites') return 'Liquidités';
                          if (s.type === 'refinancement') {
                            const refiAsset = allImmobilierAssets.find(a => a.id === s.refiAssetId);
                            return `Refi ${refiAsset?.name || 'immeuble'}`;
                          }
                          if (s.type === 'collateral_bancaire') {
                            return `Collatéral ${resolveCollateralInsuranceName(s.collateralAssetId)}`;
                          }
                          return '';
                        }).filter(Boolean);
                        if (!parts.length) return null;
                        return (
                          <span className="text-xs text-primary font-medium">
                            Via: {parts.join(' + ')}
                          </span>
                        );
                      })()}
                      {event.type === 'refinancement' && event.details?.newLTV && <span className="text-xs text-muted-foreground">LTV: {event.details.newLTV}%</span>}
                      {event.type === 'vente' && event.details?.salePrice && <span className="text-xs text-muted-foreground">Vente: {formatCurrency(event.details.salePrice)}</span>}
                      {event.type === 'vente' && event.details?.acb && <span className="text-xs text-muted-foreground">ACB: {formatCurrency(event.details.acb)}</span>}
                      {event.type === 'renovation' && event.details?.renovationCost && <span className="text-xs text-muted-foreground">Coût: {formatCurrency(event.details.renovationCost)}</span>}
                      {event.type === 'renovation' && event.details?.valueIncreasePercent && <span className="text-xs text-muted-foreground">+{event.details.valueIncreasePercent}% valeur</span>}
                      {event.type === 'renovation' && event.details?.revenueIncreasePercent && <span className="text-xs text-muted-foreground">+{event.details.revenueIncreasePercent}% revenus</span>}
                      {(event.type === 'vente' || event.type === 'refinancement') && event.details?.fundDestinationId && (
                        <span className="text-xs text-primary font-medium">
                          {event.details.fundDestinationId === '__new__'
                            ? `Vers: ${event.details.newAccountName || 'Nouveau compte'}`
                            : `Vers: ${nonImmobilierAssets.find(a => a.id === event.details.fundDestinationId)?.name || 'Compte existant'}`}
                        </span>
                      )}
                      {event.type === 'produit_financier' && event.details?.initialAmount && <span className="text-xs text-muted-foreground">Montant: {formatCurrency(event.details.initialAmount)}</span>}
                      {event.type === 'changement_contributions' && Array.isArray(event.details?.changes) && event.details.changes.filter(c => c?.assetId).length > 0 && (
                        <span className="text-xs text-primary font-medium">
                          {event.details.changes.filter(c => c?.assetId).length} compte(s) modifié(s)
                        </span>
                      )}
                      {event.type === 'assurance_participative' && event.details?.totalInsurance && <span className="text-xs text-muted-foreground">Capital-décès: {formatCurrency(event.details.totalInsurance)}</span>}
                      {event.type === 'assurance_participative' && event.details?.initialSurrenderValue && <span className="text-xs text-muted-foreground">Rachat: {formatCurrency(event.details.initialSurrenderValue)}</span>}

                      {event.type === 'cashflow_direction' && (
                        <span className="text-xs text-muted-foreground">
                          {getCashflowDirectionSummary(event.details)}
                        </span>
                      )}
                      {/* KPI: % de rendement composé entre cet événement et la fin */}
                      {!isDisabled && (() => {
                        const cagr = getEventCAGR(event);
                        if (cagr === null) return null;
                        const color = cagr >= 8 ? '#16a34a' : cagr >= 4 ? '#d97706' : '#dc2626';
                        const achatDpKpi = (parseFloat(event.details?.purchasePrice) || 0) * ((parseFloat(event.details?.downPaymentPercent) || 20) / 100);
                        const achatSrcKpi = event.type === 'achat' ? getAchatDownPaymentSources(event.details || {}, achatDpKpi) : [];
                        const isByRefi = event.type === 'achat' && achatSrcKpi.some(s => s.type === 'refinancement');
                        return (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${color}15`, color }}
                            title={isByRefi ? 'Rendement sur la mise de fonds initiale (refinancement)' : 'Rendement composé annuel'}>
                            {cagr >= 0 ? '+' : ''}{cagr.toFixed(1)}% / an
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="xs" variant="outline" iconName="Pencil" onClick={() => handleEdit(event)}>Éditer</Button>
                    <Button size="xs" variant="outline" iconName="Trash2" onClick={() => onDelete(event.id)} style={{ color: 'var(--color-destructive)' }}>Supprimer</Button>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
        </div>
      )}
    </div>
  );
};

export default PlanningEvents;
